/**
 * @author jakubbujakowski
 * @since 2022-6-13
 *
 * This is a form component on steroids. It is connected to Formik & has all the cool features that we use in our forms:
 * - auto-scrolling to error field
 * - navigation via Enter on mobiles
 * Please always use this component inside <Formik/>
 */

import { Form } from "formik";
import React from "react";

import { ErrorFocus } from "@ui/Molecules";

import { useBreakpoint } from "@hooks/useBreakpoint";

interface Props {
  id?: string;
  className?: string;
  children: React.ReactNode;
  asyncErrorFields?: string[];
  scrollContainerId?: string;
}

const FormikForm = (props: Props): React.ReactElement => {
  const formRef = React.useRef<HTMLFormElement>(null);
  const { mobile, tablet } = useBreakpoint("v1");

  const isMobile = mobile || tablet;

  /**
   * Allow to navigate to next input field by pressing Enter (mobiles only)
   */
  const handleEnterNavigation = React.useCallback(
    (event: React.KeyboardEvent<HTMLFormElement>) => {
      if (!isMobile) return;

      if (
        formRef.current &&
        event.key === "Enter" &&
        (event.target as Element).nodeName === "INPUT"
      ) {
        const form = formRef.current;

        // leave only interactive input elements
        const formElements = Array.from(form.elements).filter(
          (el: HTMLInputElement) => !el.disabled && el.nodeName === "INPUT",
        );
        const index = [].indexOf.call(form, event.target);

        if (formElements.length === index + 1) {
          return;
        }

        (formElements[index + 1] as HTMLInputElement).focus();
        event.preventDefault();
      }
    },
    [],
  );

  return (
    <ErrorFocus
      asyncErrorFields={props.asyncErrorFields}
      containerId={props.scrollContainerId}
    >
      <Form
        ref={formRef}
        onKeyDown={handleEnterNavigation}
        id={props.id}
        className={props.className}
      >
        {props.children}
      </Form>
    </ErrorFocus>
  );
};

export { FormikForm };
